/**
 * FreeResins Component - Uses same styling as Products component
 * Free resins are products with freeResinValue set
 * Budget limits are controlled by backend-defined combos
 */

import React from 'react';
import { Product, SelectedFreeResin, UIText, FreeResinCombo, Currency } from './types';
import { getImageUrl, formatPrice, getEventCurrency, getVatDisplayEnabled, getVatRate, calculateGrossPrice } from './utils';

interface FreeResinsProps {
	freeResinProducts: Product[];
	selectedFreeResins: SelectedFreeResin[];
	onSelectFreeResin: (resin: SelectedFreeResin) => void;
	onRemoveFreeResin: (uniqueId: string) => void;
	maxBudget: number;
	isEnabled: boolean;
	uiText: UIText['freeResins'];
	combos: FreeResinCombo[]; // Backend-defined combos
}

// Variant state with quantity (similar to CartItem in Products)
interface VariantItem {
	variant: string;
	quantity: number;
}

/**
 * Check if current selection matches a combo's requirements
 * @returns The matched combo with highest budget, or null if none matched
 */
const getMatchedCombo = (
	selectedFreeResins: SelectedFreeResin[],
	combos: FreeResinCombo[]
): FreeResinCombo | null => {
	if (combos.length === 0) {
		return null;
	}

	// If no products selected, no combo can be matched
	if (selectedFreeResins.length === 0) {
		return null;
	}

	// Group selected resins by product ID
	const selectionCounts: Record<string, number> = {};
	selectedFreeResins.forEach((resin) => {
		const key = resin.resinId;
		selectionCounts[key] = (selectionCounts[key] || 0) + 1;
	});

	// Find all matching combos
	const matchingCombos = combos.filter((combo) => {
		const comboProducts = combo.products;

		// Skip combos with no products defined
		if (!comboProducts || comboProducts.length === 0) {
			return false;
		}

		// Check if selection contains at least the required combo products
		for (const product of comboProducts) {
			const selectedQty = selectionCounts[product.id] || 0;
			if (selectedQty < product.quantity) {
				return false;
			}
		}
		return true;
	});

	// Return the combo with the highest budget
	if (matchingCombos.length > 0) {
		return matchingCombos.reduce((prev, current) =>
			current.budget > prev.budget ? current : prev
		);
	}

	return null;
};

/**
 * Get all product IDs that are part of any combo
 */
const getComboProductIds = (combos: FreeResinCombo[]): Set<string> => {
	const ids = new Set<string>();
	combos.forEach(combo => {
		combo.products.forEach(p => ids.add(p.id));
	});
	return ids;
};

export const FreeResins: React.FC<FreeResinsProps> = ({
	freeResinProducts,
	selectedFreeResins,
	onSelectFreeResin,
	onRemoveFreeResin,
	maxBudget,
	isEnabled,
	uiText,
	combos,
}) => {
	const comboProductIds = getComboProductIds(combos);
	const matchedCombo = getMatchedCombo(selectedFreeResins, combos);

	// Calculate budgets
	const comboProductValue = selectedFreeResins.reduce((sum, resin) => {
		return comboProductIds.has(resin.resinId) ? sum + resin.value : sum;
	}, 0);
	const nonComboProductValue = selectedFreeResins.reduce((sum, resin) => {
		return comboProductIds.has(resin.resinId) ? sum : sum + resin.value;
	}, 0);

	// Only use combo budget if combo is properly matched (has products and all requirements met)
	const isValidCombo = matchedCombo && matchedCombo.products && matchedCombo.products.length > 0;
	const effectiveMaxBudget = isValidCombo ? matchedCombo.budget : maxBudget;
	const usedBudget = comboProductValue + nonComboProductValue;
	const budgetPercentage = (usedBudget / effectiveMaxBudget) * 100;

	// Group selected resins by product and variant to get quantities
	const getVariantQuantities = (productId: string): VariantItem[] => {
		const items = selectedFreeResins.filter((r) => r.resinId === productId);
		const variantMap = new Map<string, number>();

		items.forEach((item) => {
			const key = item.variant || 'default';
			variantMap.set(key, (variantMap.get(key) || 0) + 1);
		});

		return Array.from(variantMap.entries()).map(([variant, quantity]) => ({
			variant: variant === 'default' ? undefined : variant,
			quantity,
		}));
	};

	const hasVariants = (product: Product): boolean => {
		return !!(product.variants && product.variants.length > 0);
	};

	/**
	 * Check if a product can be added based on combo rules:
	 * - If combo would be matched after adding: only combo products allowed, check combo budget
	 * - If no combo matched: all products allowed, check default budget
	 */
	const canAddProduct = (product: Product, variant?: string): boolean => {
		const currentValue = product.freeResinValue || product.price;

		// Simulate the new selection
		const simulatedSelection = [...selectedFreeResins, {
			resinId: product.id,
			name: product.name,
			value: currentValue,
			variant: hasVariants(product) ? variant : undefined,
			uniqueId: `simulated`,
		}];

		const simulatedMatchedCombo = getMatchedCombo(simulatedSelection, combos);
		const simulatedIsValid = simulatedMatchedCombo && simulatedMatchedCombo.products && simulatedMatchedCombo.products.length > 0;

		// Calculate total value for simulated selection
		const totalValue = simulatedSelection.reduce((sum, resin) => sum + resin.value, 0);

		if (simulatedIsValid && comboProductIds.size > 0) {
			// Adding this would complete a combo and combo has products configured
			// Only allow products that are part of the combo
			const isComboProduct = comboProductIds.has(product.id);
			if (!isComboProduct) {
				return false;
			}
			// Check against combo budget (only count combo products toward budget)
			const comboValue = simulatedSelection.reduce((sum, resin) => {
				return comboProductIds.has(resin.resinId) ? sum + resin.value : sum;
			}, 0);
			return comboValue <= simulatedMatchedCombo.budget;
		} else {
			// No combo matched yet, or combo not properly configured
			// Check against default budget
			return totalValue <= maxBudget;
		}
	};

	const handleQuantityChange = (
		product: Product,
		newQuantity: number,
		variant?: string
	) => {
		const quantity = Math.max(0, parseInt(newQuantity.toString()) || 0);
		const variantKey = hasVariants(product) ? variant : undefined;

		const currentItems = selectedFreeResins.filter(
			(r) => r.resinId === product.id && r.variant === variantKey
		);
		const currentQuantity = currentItems.length;
		const currentValue = product.freeResinValue || product.price;
		const isComboProduct = comboProductIds.has(product.id);

		// Simulate the new selection
		const simulatedSelection = [...selectedFreeResins];
		// Remove current items of this product+variant
		currentItems.forEach(item => {
			const idx = simulatedSelection.findIndex(s => s.uniqueId === item.uniqueId);
			if (idx !== -1) simulatedSelection.splice(idx, 1);
		});
		// Add new items
		for (let i = 0; i < quantity; i++) {
			simulatedSelection.push({
				resinId: product.id,
				name: product.name,
				value: currentValue,
				variant: variantKey,
				uniqueId: `simulated-${i}`,
			});
		}

		const simulatedMatchedCombo = getMatchedCombo(simulatedSelection, combos);
		const simulatedIsValid = simulatedMatchedCombo && simulatedMatchedCombo.products && simulatedMatchedCombo.products.length > 0;

		// If combo is currently active and we're trying to add a non-combo product, block it
		if (isValidCombo && comboProductIds.size > 0 && !isComboProduct && quantity > currentQuantity) {
			return;
		}

		// Calculate total value and check budget
		const totalValue = simulatedSelection.reduce((sum, resin) => sum + resin.value, 0);

		if (simulatedIsValid && comboProductIds.size > 0) {
			// Combo would be matched: check combo budget for combo products
			const comboValue = simulatedSelection.reduce((sum, resin) => {
				return comboProductIds.has(resin.resinId) ? sum + resin.value : sum;
			}, 0);
			// Check if any non-combo products would be present
			const hasNonComboProducts = simulatedSelection.some(resin => !comboProductIds.has(resin.resinId));
			if (hasNonComboProducts) {
				return; // Non-combo products not allowed with combo
			}
			if (comboValue > simulatedMatchedCombo.budget) {
				return; // Exceeds combo budget
			}
		} else {
			// No combo matched: check default budget
			if (totalValue > maxBudget) {
				return; // Exceeds default budget
			}
		}

		if (quantity > currentQuantity) {
			// Add instances
			const toAdd = quantity - currentQuantity;
			for (let i = 0; i < toAdd; i++) {
				onSelectFreeResin({
					resinId: product.id,
					name: product.name,
					value: currentValue,
					variant: variantKey,
					uniqueId: `${product.id}-${variantKey || 'default'}-${Date.now()}-${i}`,
				});
			}
		} else if (quantity < currentQuantity) {
			// Remove instances (most recent first)
			const toRemove = currentQuantity - quantity;
			const itemsToRemove = currentItems.slice(-toRemove);

			itemsToRemove.forEach((item) => {
				if (item.uniqueId) {
					onRemoveFreeResin(item.uniqueId);
				}
			});
		}
	};

	const handleVariantSelect = (product: Product, variant: string) => {
		const variantKey = variant;
		const currentItems = selectedFreeResins.filter(
			(r) => r.resinId === product.id && r.variant === variantKey
		);

		// Only create a new row if this variant doesn't exist yet AND budget allows
		if (currentItems.length === 0) {
			if (!canAddProduct(product, variantKey)) {
				return; // Budget exceeded
			}

			const currentValue = product.freeResinValue || product.price;
			onSelectFreeResin({
				resinId: product.id,
				name: product.name,
				value: currentValue,
				variant: variantKey,
				uniqueId: `${product.id}-${variantKey}-${Date.now()}`,
			});
		}
	};

	const handleIncrement = (product: Product, variant?: string) => {
		const variantKey = hasVariants(product) ? variant : undefined;
		const currentItems = selectedFreeResins.filter(
			(r) => r.resinId === product.id && r.variant === variantKey
		);
		const currentQuantity = currentItems.length;
		handleQuantityChange(product, currentQuantity + 1, variantKey);
	};

	const handleDecrement = (product: Product, variant?: string) => {
		const variantKey = hasVariants(product) ? variant : undefined;
		const currentItems = selectedFreeResins.filter(
			(r) => r.resinId === product.id && r.variant === variantKey
		);
		const currentQuantity = currentItems.length;
		if (currentQuantity > 0) {
			handleQuantityChange(product, currentQuantity - 1, variantKey);
		}
	};

	const getProductImageUrl = (product: Product) => {
		if (product.imagePath) {
			return getImageUrl(product.imagePath);
		}
		return `https://placehold.co/80x80/e0e0e0/666666?text=${encodeURIComponent(product.name)}`;
	};

	if (!isEnabled) {
		return (
			<section className="sprintry-free-resins sprintry-free-resins--disabled">
				<div className="sprintry-free-resins__disabled-message">
					<p>{uiText.disabledMessage}</p>
				</div>
			</section>
		);
	}

	// Get currency from free resin products or event default
	const freeResinCurrency: Currency = (() => {
		if (freeResinProducts.length > 0 && freeResinProducts[0].currency) {
			return freeResinProducts[0].currency;
		}
		return getEventCurrency();
	})();

	return (
		<section className="sprintry-free-resins">
			<div className="sprintry-free-resins__header">
				<h3 className="sprintry-free-resins__title">{uiText.title}</h3>
				<p className="sprintry-free-resins__subtitle">{uiText.subtitle}</p>
				{isValidCombo && (
					<p className="sprintry-free-resins__combo-badge">
						🎁 {matchedCombo.name} activated - Limit: {formatPrice(matchedCombo.budget, freeResinCurrency)}
					</p>
				)}
			</div>

			{/* Budget Tracker */}
			<div className="sprintry-free-resins__budget">
				<div className="sprintry-free-resins__budget-info">
					<span className="sprintry-free-resins__budget-label">
						{uiText.budget}:
					</span>
					<span className="sprintry-free-resins__budget-amount">
						{isValidCombo ? (
							<>
								{formatPrice(comboProductValue, freeResinCurrency)} / {formatPrice(matchedCombo.budget, freeResinCurrency)}
							</>
						) : (
							<>
								{formatPrice(usedBudget, freeResinCurrency)} / {formatPrice(effectiveMaxBudget, freeResinCurrency)}
							</>
						)}
					</span>
					{!isValidCombo && (
						<span className="sprintry-free-resins__remaining">
							{uiText.remaining}: {formatPrice(effectiveMaxBudget - usedBudget, freeResinCurrency)}
						</span>
					)}
				</div>
				<div className="sprintry-free-resins__budget-bar">
					<div
						className="sprintry-free-resins__budget-fill"
						style={{ width: `${isValidCombo ? 100 : Math.min(budgetPercentage, 100)}%` }}
					/>
				</div>
			</div>

			{/* Free Resin Products - same styling as Products */}
			<div className="sprintry-products">
				<div className="sprintry-products__list">
					{freeResinProducts.map((product) => {
						const variantQuantities = getVariantQuantities(product.id);
						const isComboProduct = comboProductIds.has(product.id);
						// Only disable non-combo products when a valid combo is active and has products configured
						const isDisabled = isValidCombo && comboProductIds.size > 0 && !isComboProduct;

						return (
							<div
								key={product.id}
								className={`sprintry-product-card sprintry-product-card--horizontal ${isDisabled ? 'sprintry-product-card--disabled' : ''}`}
							>
								{/* Thumbnail */}
								<div
									className="sprintry-product-card__thumbnail"
									style={{ backgroundImage: `url(${getProductImageUrl(product)})` }}
								></div>

								{/* Info */}
								<div className="sprintry-product-card__info">
									<div className="sprintry-product-card__header">
										<div className="sprintry-product-card__title-section">
											<h4 className="sprintry-product-card__name">{product.name}</h4>
											<p className="sprintry-product-card__serial-number">
												{product.serialNumber || ''}
											</p>
										</div>
										<div className="sprintry-product-card__price-section">
											<div className="sprintry-product-card__price">
												{getVatDisplayEnabled() ? (
													<span>
														{formatPrice(calculateGrossPrice(product.freeResinValue || product.price, getVatRate()), product.currency || freeResinCurrency)}
														<span className="sprintry-product-card__price-label">
															{uiText?.vatIncluded || 'incl. VAT'}
														</span>
													</span>
												) : (
													formatPrice(product.freeResinValue || product.price, product.currency || freeResinCurrency)
												)}
											</div>
											{getVatDisplayEnabled() && (
												<div className="sprintry-product-card__vat-price">
													{formatPrice(product.freeResinValue || product.price, product.currency || freeResinCurrency)} {uiText?.vatExcluded || 'excl. VAT'}
												</div>
											)}
										</div>
									</div>

									{/* Variants and Quantity */}
									{hasVariants(product) ? (
										<>
											{/* Render variant rows for each variant with quantity */}
											{variantQuantities.map((vq) => {
												const variantLabel =
													product.variants?.find(
														(v) => v.value === vq.variant
													)?.label || vq.variant;

												return (
													<div
														key={vq.variant || 'default'}
														className="sprintry-product-card__variant-row"
													>
														<select
															className="sprintry-product-card__variant"
															value={vq.variant}
															onChange={(e) => {
																const newVariant = e.target.value;
																handleQuantityChange(product, 0, vq.variant);
																handleQuantityChange(product, vq.quantity, newVariant);
															}}
														>
															{product.variants!.map((v) => (
																<option key={v.value} value={v.value}>
																	{v.label}
																</option>
															))}
														</select>
														<div className="sprintry-product-card__quantity-controls">
															<button
																type="button"
																className="sprintry-product-card__stepper-btn"
																onClick={() => handleDecrement(product, vq.variant)}
																disabled={vq.quantity === 0}
																aria-label="Decrease quantity"
															>
																−
															</button>
															<button
																type="button"
																className="sprintry-product-card__stepper-btn"
																onClick={() => handleIncrement(product, vq.variant)}
																disabled={!canAddProduct(product, vq.variant)}
																aria-label="Increase quantity"
															>
																+
															</button>
															<input
																type="number"
																className="sprintry-product-card__quantity-input"
																min="0"
																value={vq.quantity === 0 ? '' : vq.quantity}
																placeholder="0"
																onChange={(e) => {
																	const value = e.target.value;
																	if (value === '') {
																		handleQuantityChange(product, 0, vq.variant);
																	} else {
																		handleQuantityChange(product, parseInt(value) || 0, vq.variant);
																	}
																}}
																onFocus={(e) => e.target.select()}
																onClick={(e) => (e.target as HTMLInputElement).select()}
															/>
														</div>
													</div>
												);
											})}

											{/* Show default variant row if no variants selected */}
											{variantQuantities.length === 0 && (() => {
												const firstVariant = product.variants![0].value;
												return (
													<div className="sprintry-product-card__variant-row">
														<select
															className="sprintry-product-card__variant"
															value={firstVariant}
															onChange={(e) => {
																const newVariant = e.target.value;
																if (newVariant !== firstVariant && canAddProduct(product, newVariant)) {
																	handleVariantSelect(product, newVariant);
																}
															}}
														>
															{product.variants!.map((v) => (
																<option
																	key={v.value}
																	value={v.value}
																	disabled={v.value !== firstVariant && !canAddProduct(product, v.value)}
																>
																	{v.label}
																</option>
															))}
														</select>
														<div className="sprintry-product-card__quantity-controls">
															<button
																type="button"
																className="sprintry-product-card__stepper-btn"
																onClick={() => handleDecrement(product, firstVariant)}
																disabled={true}
																aria-label="Decrease quantity"
															>
																−
															</button>
															<button
																type="button"
																className="sprintry-product-card__stepper-btn"
																onClick={() => handleIncrement(product, firstVariant)}
																disabled={!canAddProduct(product, firstVariant)}
																aria-label="Increase quantity"
															>
																+
															</button>
															<input
																type="number"
																className="sprintry-product-card__quantity-input"
																min="0"
																value=""
																placeholder="0"
																onChange={(e) => {
																	const value = e.target.value;
																	if (value === '') {
																		handleQuantityChange(product, 0, firstVariant);
																	} else {
																		handleQuantityChange(product, parseInt(value) || 0, firstVariant);
																	}
																}}
																onFocus={(e) => e.target.select()}
																onClick={(e) => (e.target as HTMLInputElement).select()}
															/>
														</div>
													</div>
												);
											})()}

											{/* Show "add new variant" row when there are variants not yet selected */}
											{variantQuantities.length > 0 &&
												product.variants!.filter(
													(v) =>
														!variantQuantities.some(
															(vq) => vq.variant === v.value
														)
												).length > 0 && (
													<div className="sprintry-product-card__variant-row">
														<select
															className="sprintry-product-card__variant"
															onChange={(e) => {
																const variant = e.target.value;
																handleVariantSelect(product, variant);
																e.target.value = "";
															}}
															defaultValue=""
														>
															<option value="" disabled>
																{uiText.selectVariant || 'Select variant...'}
															</option>
															{product.variants!
																.filter(
																	(v) =>
																		!variantQuantities.some(
																			(vq) => vq.variant === v.value
																		)
																)
																.map((v) => (
																	<option
																		key={v.value}
																		value={v.value}
																		disabled={!canAddProduct(product, v.value)}
																	>
																		{v.label}
																	</option>
																))}
														</select>
														<div className="sprintry-product-card__quantity-controls">
															<button
																type="button"
																className="sprintry-product-card__stepper-btn"
																disabled
																aria-label="Decrease quantity"
															>
																−
															</button>
															<button
																type="button"
																className="sprintry-product-card__stepper-btn"
																disabled
																aria-label="Increase quantity"
															>
																+
															</button>
															<input
																type="number"
																className="sprintry-product-card__quantity-input"
																min="0"
																value=""
																placeholder="0"
																disabled
															/>
														</div>
													</div>
												)}
										</>
									) : (
										/* Products without variants - simple quantity row */
										<div className="sprintry-product-card__quantity-row">
											<div className="sprintry-product-card__quantity-controls">
												<button
													type="button"
													className="sprintry-product-card__stepper-btn"
													onClick={() => handleDecrement(product)}
													disabled={variantQuantities.length === 0 || variantQuantities[0]?.quantity === 0}
													aria-label="Decrease quantity"
												>
													−
												</button>
												<button
													type="button"
													className="sprintry-product-card__stepper-btn"
													onClick={() => handleIncrement(product)}
													disabled={!canAddProduct(product, undefined)}
													aria-label="Increase quantity"
												>
													+
												</button>
												<input
													type="number"
													className="sprintry-product-card__quantity-input"
													min="0"
													value={variantQuantities.length === 0 || variantQuantities[0]?.quantity === 0 ? '' : variantQuantities[0]?.quantity || 0}
													placeholder="0"
													onChange={(e) => {
														const value = e.target.value;
														if (value === '') {
															handleQuantityChange(product, 0);
														} else {
															handleQuantityChange(product, parseInt(value) || 0);
														}
													}}
													onFocus={(e) => e.target.select()}
													onClick={(e) => (e.target as HTMLInputElement).select()}
												/>
											</div>
										</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
};
